<app-megamenu></app-megamenu>

<div class="page-content">
  <div class="container">
    <h1>Terms of Service</h1>
    <p class="last-updated">Last updated: March 24, 2026</p>
    
    <div class="terms-content">
      <section class="terms-section">
        <h2>Welcome to {{ config.name }}</h2>
        <p>
          Thank you for choosing {{ config.name }} for your pest control needs. These Terms of Service 
          ("Terms") govern your use of our website and services. By using our services or website, 
          you agree to these terms.
        </p>
      </section>

      <section class="terms-section">
        <h2>Our Services</h2>
        <p>{{ config.name }} provides professional pest control services including:</p>
        <ul>
          <li>Residential pest control and extermination</li>
          <li>Commercial pest management solutions</li>
          <li>Preventive pest control treatments</li>
          <li>Emergency pest control services</li>
          <li>Pest inspection and consultation services</li>
        </ul>
        <p>
          All services are performed by licensed and insured technicians in accordance with 
          Utah state regulations and industry best practices.
        </p>
      </section>

      <section class="terms-section">
        <h2>Service Agreements and Scheduling</h2>
        
        <h3>Service Appointments</h3>
        <ul>
          <li>Service appointments are scheduled based on mutual availability</li>
          <li>We will make reasonable efforts to arrive within the scheduled time window</li>
          <li>Weather conditions or emergencies may require rescheduling</li>
          <li>You will receive advance notice of any schedule changes when possible</li>
        </ul>

        <h3>Access to Property</h3>
        <ul>
          <li>You must provide safe and reasonable access to treatment areas</li>
          <li>Someone 18 years or older should be present during service</li>
          <li>Please secure pets and inform us of any special considerations</li>
          <li>Clear access to treatment areas helps ensure effective service</li>
        </ul>

        <h3>Service Preparations</h3>
        <ul>
          <li>Follow any pre-service preparation instructions we provide</li>
          <li>Remove or cover food, dishes, and personal items as directed</li>
          <li>Ensure children and pets are kept away from treatment areas</li>
          <li>Notify us of any health conditions or sensitivities</li>
        </ul>
      </section>

      <section class="terms-section">
        <h2>Payment Terms</h2>
        
        <h3>Pricing and Billing</h3>
        <ul>
          <li>Service prices are as quoted and agreed upon in your service agreement</li>
          <li>Payment is due upon completion of service unless other arrangements are made</li>
          <li>We accept cash, check, and major credit cards</li>
          <li>Recurring services may be billed automatically with your consent</li>
        </ul>

        <h3>Late Payments</h3>
        <ul>
          <li>Late fees may apply to overdue accounts (as permitted by law)</li>
          <li>Services may be suspended for accounts more than 30 days past due</li>
          <li>We will work with you to resolve payment issues when possible</li>
        </ul>
      </section>

      <section class="terms-section">
        <h2>Service Guarantee</h2>
        <div class="guarantee-highlight">
          <h3>Our 30-Day Guarantee</h3>
          <p>
            If you see pests within 30 days of any service, we'll come back for free! 
            This guarantee applies to our regular pest control treatments and is subject to:
          </p>
          <ul>
            <li>Following all post-service instructions</li>
            <li>Maintaining reasonable sanitation standards</li>
            <li>No structural changes that affect treatment areas</li>
            <li>Reporting issues within the 30-day period</li>
          </ul>
        </div>
      </section>

      <section class="terms-section">
        <h2>Cancellation and Refunds</h2>
        
        <h3>Service Cancellations</h3>
        <ul>
          <li>Please provide at least 24 hours notice for appointment cancellations</li>
          <li>Same-day cancellations may be subject to a service fee</li>
          <li>Emergency situations will be handled on a case-by-case basis</li>
        </ul>

        <h3>Contract Cancellations</h3>
        <ul>
          <li>Ongoing service contracts may be cancelled with 30 days written notice</li>
          <li>Refunds for prepaid services will be prorated based on services received</li>
          <li>Cancellation terms may vary based on your specific service agreement</li>
        </ul>
      </section>

      <section class="terms-section">
        <h2>Limitations and Responsibilities</h2>
        
        <h3>Service Limitations</h3>
        <ul>
          <li>Pest control results may vary based on infestation severity and environmental factors</li>
          <li>Some pest problems may require multiple treatments for complete resolution</li>
          <li>We cannot guarantee elimination of all pests in all circumstances</li>
          <li>Structural repairs or modifications are not included in pest control services</li>
        </ul>

        <h3>Customer Responsibilities</h3>
        <ul>
          <li>Provide accurate information about pest problems and property conditions</li>
          <li>Follow all safety instructions and post-treatment guidelines</li>
          <li>Maintain reasonable sanitation and property maintenance standards</li>
          <li>Report any concerns or adverse reactions immediately</li>
        </ul>
      </section>

      <section class="terms-section">
        <h2>Safety and Liability</h2>
        
        <h3>Product Safety</h3>
        <p>
          We use EPA-approved products and follow all safety protocols. However:
        </p>
        <ul>
          <li>Always follow post-treatment instructions for your safety</li>
          <li>Keep children and pets away from treated areas as directed</li>
          <li>Notify us immediately of any adverse reactions or concerns</li>
          <li>Do not disturb or remove treatment materials without permission</li>
        </ul>

        <h3>Liability Limitations</h3>
        <p>
          {{ config.name }} carries appropriate insurance for our services. Our liability 
          is limited to the cost of the services provided, except where prohibited by law. 
          We are not responsible for:
        </p>
        <ul>
          <li>Damage caused by failure to follow instructions</li>
          <li>Pre-existing property damage or conditions</li>
          <li>Indirect or consequential damages</li>
          <li>Issues arising from customer negligence</li>
        </ul>
      </section>

      <section class="terms-section">
        <h2>Website Use</h2>
        
        <h3>Acceptable Use</h3>
        <p>When using our website, you agree to:</p>
        <ul>
          <li>Provide accurate information when requesting services</li>
          <li>Not use the site for any unlawful purposes</li>
          <li>Respect our intellectual property rights</li>
          <li>Not attempt to disrupt or damage our website</li>
        </ul>

        <h3>Content and Privacy</h3>
        <ul>
          <li>Information you provide may be used to deliver and improve our services</li>
          <li>We protect your privacy in accordance with our Privacy Policy</li>
          <li>Website content is protected by copyright and other intellectual property laws</li>
        </ul>
      </section>

      <section class="terms-section">
        <h2>Dispute Resolution</h2>
        <p>
          We're committed to resolving any issues quickly and fairly. If you have concerns:
        </p>
        <ol>
          <li>Contact us directly to discuss the issue</li>
          <li>We'll work with you to find a reasonable solution</li>
          <li>If needed, disputes may be resolved through mediation</li>
          <li>These terms are governed by Utah state law</li>
        </ol>
      </section>

      <section class="terms-section">
        <h2>Changes to Terms</h2>
        <p>
          We may update these Terms of Service from time to time to reflect changes in our 
          services or legal requirements. When we do:
        </p>
        <ul>
          <li>We'll post the updated terms on our website</li>
          <li>The "Last updated" date will be revised</li>
          <li>Continued use of our services constitutes acceptance of new terms</li>
          <li>We'll notify you of significant changes when possible</li>
        </ul>
      </section>

      <section class="terms-section">
        <h2>Contact Information</h2>
        <p>
          If you have questions about these Terms of Service or need to discuss any 
          service-related matters, please contact us:
        </p>
        <div class="contact-info">
          <p><strong>{{ config.name }}</strong></p>
          <p>Phone: {{ config.phone.display }}</p>
          <p>Email: {{ config.email }}</p>
          <p>Address: {{ config.address.full }}</p>
          <p>Hours: {{ config.hours.weekdays }} | Saturday: {{ config.hours.saturday }}</p>
        </div>
        <p>
          We're here to help and want to ensure you have a positive experience with our services.
        </p>
      </section>
    </div>
  </div>
</div>

<app-footer></app-footer>